// 🎨 테마 (2026-09-07 SettingsModal 에서 분리) — 부팅(main.tsx)·설정 모달·기기 간 동기화(prefsSync)가 같은 함수를 쓴다.
//   컴포넌트 파일에 있으면 api 모듈이 설정 모달 전체를 끌어와야 해서 여기로 옮겼다. SettingsModal 은 re-export 로 종전 import 유지.
import { diagLog } from '../desktop/diag';
import { markPrefChanged } from './prefs';

// 3대 레일 테마 (2026-06-10 개편) — 라이트 본문 공통 + 상단 레일 컬러만 분기. 'black' = 유일한 다크 계열 (2026-07-09).
export type Theme = 'navy' | 'blue' | 'red' | 'black' | 'navyrail' | 'wine' | 'cobalt';   // wine="Wine Red"(와인 크롬+웜 화이트 사이드바) · cobalt="Cobalt Blue"(코발트 크롬+옅은 블루 사이드바) 2026-09-20   // navyrail = "Navy Rail" (2026-09-19) — 네이비 크롬(상단바+사이드바) + 라이트 본문

export const THEME_KEY = 'tpt-theme';
export const THEME_DEFAULT: Theme = 'navy';

// 레거시 테마명(light/dark/friendly/business/minimal) → navy(기본) 로 정규화.
export function normalizeTheme(v: string | null | undefined): Theme {
	return v === 'blue' || v === 'red' || v === 'black' || v === 'navyrail' || v === 'wine' || v === 'cobalt' ? v : THEME_DEFAULT;
}

export function readSavedTheme(): Theme {
	// 디폴트: navy(딥 네이비 레일). 저장된 레거시 값은 normalizeTheme 가 흡수.
	if (typeof localStorage === 'undefined') return THEME_DEFAULT;
	return normalizeTheme(localStorage.getItem(THEME_KEY));
}

/** 문서에 테마 속성만 적용(저장 안 함) — 팝아웃 창이 storage 이벤트로 따라올 때 쓴다(다시 저장하면 창 간 핑퐁). */
export function paintTheme(t: Theme, source: string): void {
	if (typeof document === 'undefined') return;
	const norm = normalizeTheme(t);
	const root = document.documentElement;
	const prev = root.getAttribute('data-theme');
	// 🎬 테마 전환 크로스페이드 (2026-06-10) — 이미 테마가 적용된 상태에서 '다른' 테마로 바뀔 때만
	// 0.3s 동안 transition 클래스를 부여해 색이 부드럽게 넘어가게 한다 (첫 부팅 적용은 즉시).
	if (prev && prev !== norm) {
		root.classList.add('tpt-theme-anim');
		window.setTimeout(() => root.classList.remove('tpt-theme-anim'), 300);
	}
	root.setAttribute('data-theme', norm);
	// 📱 theme-color 메타 = 테마의 레일(크롬) 색 (2026-09-19) — index.html 정적값은 navy 고정이라 다른 테마에서 상태바 색이 어긋났다.
	try {
		const THEME_COLOR: Record<Theme, string> = { navy: '#12225A', blue: '#2F6BED', red: '#E5484D', black: '#161512', navyrail: '#0F1D3D', wine: '#5E1310', cobalt: '#0B3F89' };
		const meta = document.querySelector('meta[name="theme-color"]');
		if (meta) meta.setAttribute('content', THEME_COLOR[norm]);
	} catch { /* 메타 없음 */ }
	// 🍎 네이티브 셸 통지 (2026-09-14 iOS Q) — iOS 는 웹뷰를 safe area 안쪽에 두고 그 바깥 띠를
	//   네이티브 배경색으로 칠한다. 웹 테마가 다크로 바뀌면 그 띠만 밝게 남으므로, 셸이 따라올 수 있게
	//   테마명과 실제 배경색을 함께 알린다. 웹 전용 환경에서는 듣는 쪽이 없어 무해하다(이벤트 1회 발사뿐).
	//   🔴 색은 이름이 아니라 **계산된 값**을 보낸다 — 테마별 CSS 변수를 셸이 다시 해석하지 않게.
	if (prev !== norm) {
		try {
			const bg = getComputedStyle(document.body).backgroundColor || '';
			window.dispatchEvent(new CustomEvent('tpt:theme', { detail: { theme: norm, background: bg } }));
		} catch { /* 렌더 전이면 건너뛴다 — 셸은 로드 완료 후 1회 직접 조회하면 된다 */ }
	}
	// 🔎 진단 (2026-09-07 사용자 "데탑 테마가 블루였는데 네이비로 되기도") — 바뀐 경위를 logs/tptalk.log 에 남긴다.
	if (prev !== norm) diagLog('theme', `${prev ?? '(none)'} → ${norm}`, `by=${source}`);
}

/** 테마 적용 + 저장. source 는 진단 로그용(boot/user/sync). */
export function applyTheme(t: Theme, source = 'user'): void {
	const norm = normalizeTheme(t);
	paintTheme(norm, source);
	try { localStorage.setItem(THEME_KEY, norm); } catch { /* private mode */ }
	markPrefChanged(THEME_KEY);
}
